import { useEffect, useState } from "react";

import { api, type UsageSnapshot } from "../lib/api.ts";
import { getMe, logout, type User } from "../lib/auth.ts";

export function Settings() {
	const [user, setUser] = useState<User | null>(null);
	const [usage, setUsage] = useState<UsageSnapshot | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		void getMe().then(setUser);
		void api
			.usage()
			.then(setUsage)
			.catch((e) => setError((e as Error).message));
	}, []);

	const maxDayCost = Math.max(...(usage?.last7Days.map((d) => d.estCostUsd) ?? [0.001]), 0.001);

	return (
		<div className="px-6 py-8 max-w-3xl space-y-8">
			<h1 className="text-xl font-semibold">Settings</h1>

			<section>
				<h2 className="text-sm font-medium text-zinc-400 mb-2">Session</h2>
				<div className="border border-zinc-800 rounded-md bg-zinc-900/50 p-4 text-sm">
					{user ? (
						<dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-4">
							<dt className="text-zinc-500">User ID</dt>
							<dd className="font-mono text-xs">{user.userId}</dd>
							<dt className="text-zinc-500">Role</dt>
							<dd>{user.role ?? "—"}</dd>
							<dt className="text-zinc-500">Session started</dt>
							<dd>{user.sessionCreatedAt}</dd>
						</dl>
					) : (
						<div className="text-zinc-500">Loading…</div>
					)}
				</div>
				<button
					type="button"
					onClick={() => void logout()}
					className="mt-3 px-3 py-1.5 text-sm rounded-md bg-zinc-800 hover:bg-zinc-700"
				>
					Sign out
				</button>
			</section>

			<section>
				<h2 className="text-sm font-medium text-zinc-400 mb-2">Anthropic API usage</h2>
				{error && (
					<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-3">
						{error}
					</div>
				)}
				<div className="border border-zinc-800 rounded-md bg-zinc-900/50 p-4">
					{usage === null ? (
						<div className="text-sm text-zinc-500">Loading…</div>
					) : (
						<>
							<div className="grid grid-cols-4 gap-4 text-sm mb-6">
								<div>
									<div className="text-xs uppercase tracking-wider text-zinc-500">Requests</div>
									<div className="text-2xl font-semibold">{usage.totals.requestCount}</div>
								</div>
								<div>
									<div className="text-xs uppercase tracking-wider text-zinc-500">Input tokens</div>
									<div className="text-2xl font-semibold">{(usage.totals.inputTokens / 1000).toFixed(1)}K</div>
								</div>
								<div>
									<div className="text-xs uppercase tracking-wider text-zinc-500">Output tokens</div>
									<div className="text-2xl font-semibold">{(usage.totals.outputTokens / 1000).toFixed(1)}K</div>
								</div>
								<div>
									<div className="text-xs uppercase tracking-wider text-zinc-500">Est. cost</div>
									<div className="text-2xl font-semibold text-emerald-400">
										${usage.totals.estCostUsd.toFixed(4)}
									</div>
								</div>
							</div>

							<div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Last 7 days</div>
							<div className="space-y-1.5">
								{usage.last7Days.map((d) => {
									const widthPct = (d.estCostUsd / maxDayCost) * 100;
									return (
										<div key={d.date} className="flex items-center gap-3 text-xs">
											<div className="w-20 text-zinc-500 font-mono">{d.date.slice(5)}</div>
											<div className="flex-1 relative h-5 bg-zinc-800 rounded">
												<div
													className="absolute inset-y-0 left-0 bg-accent-600 rounded"
													style={{ width: `${Math.max(widthPct, 1)}%` }}
												/>
											</div>
											<div className="w-32 text-right text-zinc-400 font-mono">
												{d.requestCount > 0 ? `${d.requestCount} req · $${d.estCostUsd.toFixed(4)}` : "—"}
											</div>
										</div>
									);
								})}
							</div>

							{usage.byModel.length > 0 && (
								<>
									<div className="text-xs uppercase tracking-wider text-zinc-500 mt-6 mb-2">By model</div>
									<table className="w-full text-xs">
										<thead className="text-zinc-500">
											<tr>
												<th className="text-left pb-1">Model</th>
												<th className="text-right pb-1">Requests</th>
												<th className="text-right pb-1">In tokens</th>
												<th className="text-right pb-1">Out tokens</th>
											</tr>
										</thead>
										<tbody>
											{usage.byModel.map((m) => (
												<tr key={m.model} className="border-t border-zinc-800">
													<td className="py-1.5 font-mono">{m.model}</td>
													<td className="py-1.5 text-right">{m.requestCount}</td>
													<td className="py-1.5 text-right">{m.inputTokens.toLocaleString()}</td>
													<td className="py-1.5 text-right">{m.outputTokens.toLocaleString()}</td>
												</tr>
											))}
										</tbody>
									</table>
								</>
							)}

							<div className="mt-4 text-xs text-zinc-600">
								In-memory ledger — resets on server restart. Cost estimates use Anthropic
								published pricing.
							</div>
						</>
					)}
				</div>
			</section>
		</div>
	);
}
